import React, { memo } from "react";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import FitText, { FitTextGroup } from "@/components/ui/FitText";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import type { RevenueLine } from "@/types/underwriting";
import { HEADER_LABEL } from "./headerStyles";
import { LeverageMeter } from "./LeverageMeter";

/** One funder's share of the MCA payout, for the payout's hover. */
export interface FunderPayout {
  id: string;
  funder: string;
  monthlyPayout: number;
}

/** The figures before a proposed change (an approval card's Impact): each shows under its new value. */
export interface DecisionStripBefore {
  revenueLines: RevenueLine[];
  avgRevenue: string | null;
  leveragePct: number;
  monthlyPayout: number;
}

export interface DecisionStripProps {
  /** The analysis's months, oldest first: the last three are the Recent Revenue tiles. */
  revenueLines: RevenueLine[];
  /** The average's display ("$148k"), or null when no month counts towards it. */
  avgRevenue: string | null;
  /** "YYYY-MM" months the average lacks, flagged beside its label. */
  missingAvgMonths?: string[];
  leveragePct: number;
  /** The org's target leverage: the meter's tick, and the figure turns red at or above it. */
  targetPct: number;
  monthlyPayout: number;
  /** The payout's by-funder hover; none when not given. */
  payoutByFunder?: FunderPayout[];
  /** What the figures were: each shows its old value struck under the new one, or "No change". */
  before?: DecisionStripBefore;
  /** The tile shown as selected (its month filters the transactions). */
  activeMonth?: string | null;
  /** Tiles, Show all and the average are buttons only where these are given. */
  onMonthClick?: (monthAbbrev: string) => void;
  onShowAllRevenue?: () => void;
  onAvgRevenueClick?: () => void;
  /**
   * "board": the two cards go straight into the dashboard header's grid (areas rev, lev), which
   * lays them out. "standalone" (default): one block that puts Leverage beside Revenue where
   * its own width allows and stacks them where it does not (a chat card, a phone).
   */
  placement?: "board" | "standalone";
}

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const formatMissingMonthLabel = (ym: string): string => {
  const [y, m] = ym.split("-");
  const mi = Number(m) - 1;
  return Number.isFinite(mi) && MONTH_NAMES[mi] ? `${MONTH_NAMES[mi]} ${y}` : ym;
};

const MISSING_AVG_LABEL = "Missing months for accurate avg revenue";

function MissingMonthsWarning({ months }: { months: string[] }) {
  if (months.length === 0) return null;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700 ring-1 ring-amber-200/80 transition-colors hover:bg-amber-200/80"
          aria-label={MISSING_AVG_LABEL}
        >
          <AlertTriangle className="h-3 w-3" aria-hidden />
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">{`${MISSING_AVG_LABEL}: ${months.map(formatMissingMonthLabel).join(", ")}`}</TooltipContent>
    </Tooltip>
  );
}

const NO_MONTHS: string[] = [];

/** A figure's old value under its new one: struck, or "No change" when the same. */
function Before({ old, now, className }: { old: string; now: string; className?: string }) {
  return old === now ? (
    <span className={cn("text-[10px] leading-none text-muted-foreground", className)}>
      No change
    </span>
  ) : (
    <span className={cn("text-[10px] leading-none text-muted-foreground", className)}>
      <span className="sr-only">was </span>
      <s>{old}</s>
    </span>
  );
}

const percentText = (pct: number) => `${Math.round(pct)}%`;
const payoutText = (amount: number) => `$${Math.round(amount).toLocaleString()}`;
const CARD = "rounded-lg border border-border/60 bg-card shadow-sm";
const TILE_HOVER = "hover:border-border hover:bg-muted";

/** The month tiles and the average: a button where it does something, otherwise plain. */
function Pressable({
  onClick,
  className,
  children,
  ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={className} {...rest}>
        {children}
      </button>
    );
  }
  return (
    <div className={className} {...(rest as React.HTMLAttributes<HTMLDivElement>)}>
      {children}
    </div>
  );
}

function RevenueCard({
  revenueLines,
  avgRevenue,
  missingAvgMonths = NO_MONTHS,
  activeMonth,
  onMonthClick,
  onShowAllRevenue,
  onAvgRevenueClick,
  before,
  className,
}: DecisionStripProps & { className?: string }) {
  const oldMonths = new Map(
    (before?.revenueLines ?? []).map((line) => [line.month_abbrev, line.amount_display])
  );
  return (
    <div className={cn("min-w-0 overflow-hidden [container:revenue/inline-size]", CARD, className)}>
      <div className="flex h-full">
        {/* The card is a container: its tiles and the average tighten below 27.5rem. */}
        <div className="flex min-w-0 flex-1 flex-col overflow-hidden px-4 py-3 [@container_revenue_(max-width:27.49rem)]:px-3">
          <div className="mb-0.5 flex items-center gap-2">
            <p className={cn(HEADER_LABEL, "mb-0")}>Recent Revenue</p>
            {revenueLines.length > 3 && onShowAllRevenue && (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                className="h-5 rounded-md px-2 text-[0.62rem] font-semibold uppercase tracking-wide text-muted-foreground shadow-none"
                onClick={onShowAllRevenue}
              >
                Show all
              </Button>
            )}
          </div>
          <FitTextGroup
            className="flex flex-1 items-center gap-2 [@container_revenue_(max-width:27.49rem)]:gap-1.5"
            maxPx={14}
            minPx={8}
          >
            {revenueLines.slice(-3).map((line) => {
              const active = line.month_abbrev === activeMonth;
              return (
                <Pressable
                  key={line.month_abbrev}
                  onClick={onMonthClick && (() => onMonthClick(line.month_abbrev))}
                  aria-pressed={onMonthClick ? active : undefined}
                  className={cn(
                    "flex min-w-0 flex-1 flex-col items-stretch overflow-hidden rounded-md border px-2.5 py-1.5 text-left transition-colors [@container_revenue_(max-width:27.49rem)]:px-1.5",
                    active
                      ? "border-primary/40 bg-primary/5 shadow-sm"
                      : cn("border-border/80 bg-muted/50", onMonthClick && TILE_HOVER)
                  )}
                >
                  <FitText className="font-bold leading-none text-foreground">
                    {line.amount_display}
                  </FitText>
                  {before && (
                    <Before
                      className="mt-1"
                      old={oldMonths.get(line.month_abbrev) ?? "—"}
                      now={line.amount_display}
                    />
                  )}
                  <span className="mt-1 text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">
                    {line.month_abbrev}
                  </span>
                </Pressable>
              );
            })}
          </FitTextGroup>
        </div>
        <div className="flex min-w-[8.5rem] shrink-0 flex-col items-start border-l border-border/40 px-4 py-3 [@container_revenue_(max-width:27.49rem)]:w-[6.25rem] [@container_revenue_(max-width:27.49rem)]:min-w-0 [@container_revenue_(max-width:27.49rem)]:px-2">
          <div className="mb-0.5 flex shrink-0 items-center gap-1">
            <p
              className={cn(
                HEADER_LABEL,
                "mb-0 whitespace-nowrap [@container_revenue_(max-width:27.49rem)]:whitespace-normal"
              )}
            >
              Avg Revenue
            </p>
            <MissingMonthsWarning months={missingAvgMonths} />
          </div>
          <div className="flex min-h-0 w-full flex-1 flex-col items-start justify-center">
            <Pressable
              data-testid="avg-revenue-chip"
              onClick={onAvgRevenueClick}
              aria-label={
                onAvgRevenueClick
                  ? `Average revenue ${avgRevenue ?? "—"}. Edit months included in average.`
                  : undefined
              }
              className={cn(
                "flex w-fit min-w-0 max-w-full flex-col items-stretch overflow-hidden rounded-md border px-3 py-1.5 transition-colors",
                "border-border/80 bg-muted/50",
                onAvgRevenueClick && TILE_HOVER
              )}
            >
              <FitText
                maxPx={22}
                minPx={11}
                className="text-center font-extrabold leading-none text-foreground"
              >
                {avgRevenue ?? "—"}
              </FitText>
              {before && (
                <Before
                  className="mt-1 text-center"
                  old={before.avgRevenue ?? "—"}
                  now={avgRevenue ?? "—"}
                />
              )}
              <span className="mt-1.5 whitespace-nowrap text-center text-[0.62rem] font-semibold uppercase tracking-wide text-muted-foreground/70">
                Per Month
              </span>
            </Pressable>
          </div>
        </div>
      </div>
    </div>
  );
}

function LeverageCard({
  leveragePct,
  targetPct,
  monthlyPayout,
  payoutByFunder,
  before,
  className,
}: DecisionStripProps & { className?: string }) {
  const payout = (
    <div className="flex cursor-default flex-col items-center">
      {/* "$" and the figure as two text nodes, shaped as before the extraction. */}
      <p className="text-[26px] font-extrabold leading-none text-foreground">
        ${Math.round(monthlyPayout).toLocaleString()}
      </p>
      {before && (
        <Before
          className="mt-1"
          old={payoutText(before.monthlyPayout)}
          now={payoutText(monthlyPayout)}
        />
      )}
      <p className="mt-1.5 whitespace-nowrap text-[0.62rem] font-semibold uppercase tracking-wide text-muted-foreground/70">
        Per Month
      </p>
    </div>
  );
  return (
    <div className={cn("flex overflow-hidden", CARD, className)}>
      <div className="flex flex-1 flex-col items-center px-4 py-3">
        <p className={cn(HEADER_LABEL, "mb-0 shrink-0 leading-none")}>Leverage</p>
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center">
          <p
            data-testid="effective-leverage"
            className={cn(
              "text-[26px] font-extrabold leading-none",
              leveragePct >= targetPct ? "text-destructive" : "text-primary"
            )}
          >
            {Math.round(leveragePct)}%
          </p>
          {before && (
            <Before
              className="mt-1"
              old={percentText(before.leveragePct)}
              now={percentText(leveragePct)}
            />
          )}
          <div className="mt-1.5 flex h-3 items-center justify-center">
            <LeverageMeter percent={leveragePct} targetPercent={targetPct} />
          </div>
        </div>
      </div>
      <div className="flex flex-1 flex-col items-center border-l border-border/40 px-4 py-3">
        <p className={cn(HEADER_LABEL, "mb-0 shrink-0 leading-none")}>MCA Payout</p>
        <div className="flex min-h-0 w-full flex-1 flex-col items-center justify-center">
          {!payoutByFunder ? (
            payout
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>{payout}</TooltipTrigger>
              <TooltipContent
                side="bottom"
                className="max-w-xs border border-border/80 bg-background/95 text-foreground shadow-md"
              >
                {payoutByFunder.length === 0 ? (
                  <p className="text-[0.7rem]">No active MCA withdrawals contributing to payout.</p>
                ) : (
                  <div className="px-1 py-1">
                    <p className="mb-1 text-[0.7rem] font-semibold uppercase">
                      Monthly payout by funder
                    </p>
                    {payoutByFunder.map((share) => (
                      <div key={share.id} className="mb-0.5 flex justify-between text-[0.7rem]">
                        <span className="mr-2">{share.funder}</span>
                        <span className="font-semibold">
                          ${Math.round(share.monthlyPayout).toLocaleString()}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * The underwriting header's figures: Recent Revenue tiles and the average, Leverage with its
 * meter, and the MCA Payout with its by-funder hover, from plain values (the dashboard's, or a
 * stored analysis's). Below 33rem of its own width the standalone strip stacks.
 */
export const DecisionStrip = memo(function DecisionStrip(props: DecisionStripProps) {
  if (props.placement === "board") {
    return (
      <TooltipProvider delayDuration={150}>
        <RevenueCard {...props} className="[grid-area:rev]" />
        {/* Tighter padding while the board's row is tight. */}
        <LeverageCard
          {...props}
          className="[grid-area:lev] [@container_board_(min-width:35.75rem)_and_(max-width:65.24rem)]:[&>*]:px-2"
        />
      </TooltipProvider>
    );
  }
  return (
    <TooltipProvider delayDuration={150}>
      <div className="[container:decision-strip/inline-size]">
        <div className="grid gap-3 [@container_decision-strip_(min-width:33rem)]:grid-cols-[minmax(0,1fr)_max-content]">
          <RevenueCard {...props} />
          <LeverageCard {...props} />
        </div>
      </div>
    </TooltipProvider>
  );
});
